import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Download, Lock, AlertCircle, FileText, Calendar, HardDrive, ShoppingCart, Loader2, Check, Tag, ShieldCheck, Volume2, VolumeX } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import BoxIconOpen from '../components/BoxIconOpen';
import { importKey, decryptBlob, isEncryptionSupported } from '../utils/encryption';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DownloadPage = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [shareInfo, setShareInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [backgrounds, setBackgrounds] = useState([]);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [previousBgIndex, setPreviousBgIndex] = useState(-1);
  const [bgLink, setBgLink] = useState('');
  const [isMuted, setIsMuted] = useState(true);
  const [isDefaultBg, setIsDefaultBg] = useState(false);
  const videoRefs = useRef({});
  
  // Purchase states
  const [downloadToken, setDownloadToken] = useState(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(false);
  const [purchaseComplete, setPurchaseComplete] = useState(false);
  
  // Encryption states
  const [encryptionKeyStr, setEncryptionKeyStr] = useState(null);
  const [decrypting, setDecrypting] = useState(false);

  useEffect(() => {
    fetchShareInfo();
    fetchBackgrounds();
    // Extract encryption key from URL fragment
    const hash = window.location.hash;
    if (hash && hash.includes('key=')) {
      const keyParam = hash.split('key=')[1]?.split('&')[0];
      if (keyParam) setEncryptionKeyStr(keyParam);
    }
  }, [shareId]);

  // Check for purchase return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const purchaseParam = searchParams.get('purchase');
    if (sessionId && purchaseParam === 'success') {
      pollPurchaseStatus(sessionId);
    }
  }, [searchParams]);

  // Slideshow timer – simple advancement, cross-fade handled via CSS
  useEffect(() => {
    if (backgrounds.length <= 1) return;
    const current = backgrounds[currentBgIndex];
    const duration = (current?.duration || 10) * 1000;

    const timer = setTimeout(() => {
      setPreviousBgIndex(currentBgIndex);
      setCurrentBgIndex((currentBgIndex + 1) % backgrounds.length);
    }, duration);

    return () => clearTimeout(timer);
  }, [currentBgIndex, backgrounds]);

  // Track background view (impression) when displayed
  useEffect(() => {
    if (backgrounds.length === 0) return;
    const bg = backgrounds[currentBgIndex];
    if (bg?.id) {
      axios.post(`${API}/share/${shareId}/background-view`, { bg_id: bg.id, is_default: isDefaultBg }).catch(() => {});
    }
  }, [currentBgIndex, backgrounds]);

  // Clear previousBgIndex after cross-fade transition ends
  useEffect(() => {
    if (previousBgIndex === -1) return;
    const timer = setTimeout(() => setPreviousBgIndex(-1), 2200);
    return () => clearTimeout(timer);
  }, [previousBgIndex]);

  // Update current link when background changes
  useEffect(() => {
    if (backgrounds.length > 0) {
      setBgLink(backgrounds[currentBgIndex]?.link || '');
    }
  }, [currentBgIndex, backgrounds]);

  // Manage video play/pause: active plays, previous keeps playing during fade, rest paused
  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([idx, el]) => {
      if (!el) return;
      const i = parseInt(idx);
      if (i === currentBgIndex) {
        el.currentTime = el.currentTime || 0;
        el.play().catch(() => {});
      } else if (i === previousBgIndex) {
        // Keep playing during cross-fade – will be paused when previousBgIndex resets
      } else {
        el.pause();
      }
    });
  }, [currentBgIndex, previousBgIndex]);

  const fetchShareInfo = async () => {
    try {
      const response = await axios.get(`${API}/share/${shareId}`);
      setShareInfo(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossible de charger les informations du partage');
      setLoading(false);
    }
  };

  const fetchBackgrounds = async () => {
    let bgs = [];
    let randomOrder = false;
    let isDefault = false;
    try {
      const response = await axios.get(`${API}/share/${shareId}/background`);
      if (response.data.has_background && response.data.backgrounds) {
        bgs = response.data.backgrounds;
        randomOrder = response.data.random_order || false;
        isDefault = response.data.is_default || false;
      }
    } catch (err) {
      console.log('No custom background, trying defaults');
    }
    if (!bgs.length) {
      try {
        const res = await axios.get(`${API}/default-backgrounds`);
        if (res.data.has_background && res.data.backgrounds) {
          bgs = res.data.backgrounds;
          isDefault = true;
        }
      } catch {}
    }
    setIsDefaultBg(isDefault);
    // Shuffle if random order is enabled
    if (randomOrder && bgs.length > 1) {
      for (let i = bgs.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [bgs[i], bgs[j]] = [bgs[j], bgs[i]];
      }
    }
    // Pre-fetch video backgrounds: small as blob URLs, large use direct streaming
    const VIDEO_BLOB_SIZE_LIMIT = 10 * 1024 * 1024; // 10MB
    const processed = await Promise.all(bgs.map(async (bg) => {
      if (bg.type === 'video') {
        const videoUrl = bg.url?.startsWith('http') ? bg.url : `${BACKEND_URL}${bg.url}`;
        try {
          // Check video size first with a HEAD request
          const head = await fetch(videoUrl, { method: 'HEAD' });
          const size = parseInt(head.headers.get('content-length') || '0', 10);
          if (size > 0 && size <= VIDEO_BLOB_SIZE_LIMIT) {
            const resp = await fetch(videoUrl);
            const blob = await resp.blob();
            const blobUrl = URL.createObjectURL(blob);
            return { ...bg, blobUrl };
          }
        } catch (e) {
          console.error('Failed to preload video:', e);
        }
        // Large or failed: use direct URL with cache-busting for streaming
        return { ...bg, blobUrl: null, streamUrl: videoUrl };
      }
      return bg;
    }));
    setBackgrounds(processed);
  };

  const pollPurchaseStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    if (attempts >= maxAttempts) {
      toast({ title: 'Erreur', description: 'Vérification du paiement expirée. Veuillez réessayer.', variant: 'destructive' });
      setPollingStatus(false);
      return;
    }
    setPollingStatus(true);
    try {
      const response = await axios.get(`${API}/payments/purchase/status/${sessionId}`);
      if (response.data.payment_status === 'paid' && response.data.download_token) {
        setDownloadToken(response.data.download_token);
        setPurchaseComplete(true);
        setPollingStatus(false);
        toast({ title: 'Paiement confirmé !', description: 'Vous pouvez maintenant télécharger le fichier.' });
      } else {
        setTimeout(() => pollPurchaseStatus(sessionId, attempts + 1), 2000);
      }
    } catch (err) {
      setTimeout(() => pollPurchaseStatus(sessionId, attempts + 1), 2000);
    }
  }, [toast]);

  const handlePurchase = async () => {
    setPurchaseLoading(true);
    try {
      const response = await axios.post(`${API}/payments/purchase/${shareId}`, {
        origin_url: window.location.origin,
        buyer_email: ''
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Impossible de créer la session de paiement', variant: 'destructive' });
      setPurchaseLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const isEncrypted = shareInfo?.is_encrypted && encryptionKeyStr;
      
      if (isEncrypted) {
        setDecrypting(false);
      }
      
      // Use fetch instead of axios to avoid blob/responseText conflict on error
      const fetchRes = await fetch(
        `${API}/download/${shareId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: password || null, download_token: downloadToken || null })
        }
      );

      if (!fetchRes.ok) {
        let errorMsg = 'Impossible de telecharger le fichier';
        try {
          const errData = await fetchRes.json();
          errorMsg = errData.detail || errorMsg;
        } catch {}
        if (fetchRes.status === 410 || fetchRes.status === 404 || fetchRes.status === 403) {
          setError(errorMsg);
          return;
        }
        throw new Error(errorMsg);
      }

      // Check if the response is a JSON with a presigned URL (non-encrypted files)
      const contentType = fetchRes.headers.get('content-type') || '';
      if (contentType.includes('application/json') && !isEncrypted) {
        const data = await fetchRes.json();
        if (data.download_url) {
          // Native browser download from presigned URL (streams directly from Wasabi)
          const link = document.createElement('a');
          link.href = data.download_url;
          link.setAttribute('download', shareInfo.filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          toast({ title: 'Telechargement lance !', description: 'Votre fichier est en cours de telechargement' });
          return;
        }
      }

      // Encrypted files: load into blob, decrypt, then save
      let blob = await fetchRes.blob();
      
      if (isEncrypted && isEncryptionSupported()) {
        setDecrypting(true);
        toast({ title: 'Dechiffrement...', description: 'Vos fichiers sont en cours de dechiffrement' });
        const cryptoKey = await importKey(encryptionKeyStr);
        blob = await decryptBlob(blob, cryptoKey, shareInfo.mime_type || 'application/octet-stream');
        setDecrypting(false);
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', shareInfo.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Telechargement lance !', description: isEncrypted ? 'Fichier dechiffre et telecharge' : 'Votre fichier est en cours de telechargement' });
    } catch (err) {
      setDecrypting(false);
      let errorMsg = err.message || 'Impossible de telecharger le fichier';
      if (err.name === 'OperationError') errorMsg = 'Cle de dechiffrement invalide. Verifiez que le lien est complet.';
      toast({ title: 'Erreur', description: errorMsg, variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' Ko';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' Mo';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' Go';
  };

  const formatDate = (dateString) => {
    const d = typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+') ? dateString + 'Z' : dateString;
    return new Date(d).toLocaleString('fr-FR');
  };

  const currencySymbol = (c) => ({ EUR: '\u20AC', USD: '$', GBP: '\u00A3' }[c] || c);

  // Background rendering helper for loading/error states
  const renderBackgrounds = () => (
    backgrounds.length > 0 ? (
      <>
        {backgrounds.map((bg, i) => {
          const isActive = i === currentBgIndex;
          const videoSrc = bg.blobUrl || (bg.url?.startsWith('http') ? bg.url : `${BACKEND_URL}${bg.url}`);
          const imgUrl = bg.url?.startsWith('http') ? bg.url : `${BACKEND_URL}${bg.url}`;
          return (
            <div
              key={bg.id || i}
              style={{
                position: 'absolute',
                inset: 0,
                opacity: isActive ? 1 : 0,
                zIndex: isActive ? 1 : 0,
                transform: 'translateZ(0)',
                visibility: isActive ? 'visible' : 'hidden',
              }}
            >
              {bg.type === 'video' ? (
                <video
                  src={videoSrc}
                  muted
                  autoPlay={isActive}
                  loop
                  playsInline
                  className="w-full h-full object-cover"
                  ref={el => { videoRefs.current[i] = el; }}
                  onLoadedData={(e) => { if (isActive) e.target.play().catch(() => {}); }}
                />
              ) : (
                <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url(${imgUrl})` }} />
              )}
            </div>
          );
        })}
        <div className="absolute inset-0 bg-black/50 z-[1]" />
      </>
    ) : (
      <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-slate-900 to-blue-900" />
    )
  );

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        {renderBackgrounds()}
        <div className="relative z-10 animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {renderBackgrounds()}
        <Toaster />
        {/* Header with Logo */}
        <header className="relative z-10 px-6 lg:px-16 xl:px-24 py-4">
          <div>
            <a href="/" className="inline-flex items-center gap-3">
              <BoxIconOpen size={44} />
              <span className="text-2xl font-bold text-white tracking-tight">SENDBOX</span>
            </a>
          </div>
        </header>
        <div className="relative z-10 min-h-[calc(100vh-80px)] flex items-center justify-center p-6">
          <div className="bg-slate-900/90 border border-red-500/30 rounded-2xl p-8 max-w-md w-full text-center backdrop-blur-sm">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Fichier Non Disponible</h2>
            <p className="text-white/60 mb-6">{error}</p>
            <Button onClick={() => navigate('/')} className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600" data-testid="go-home-btn">
              Retour à l'Accueil
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const isPaidFile = shareInfo?.is_paid && shareInfo?.price > 0;
  const canDownload = !isPaidFile || purchaseComplete;
  const isEncrypted = shareInfo?.is_encrypted && encryptionKeyStr;

  return (
    <div className="min-h-screen relative overflow-hidden">
      <Toaster />
      
      {/* Background Slideshow */}
      {backgrounds.length > 0 ? (
        <>
          {backgrounds.map((bg, i) => {
            const isActive = i === currentBgIndex;
            const isPrevious = i === previousBgIndex;
            const videoSrc = bg.blobUrl || bg.streamUrl || (bg.url?.startsWith('http') ? bg.url : `${BACKEND_URL}${bg.url}`);
            const imgUrl = bg.url?.startsWith('http') ? bg.url : `${BACKEND_URL}${bg.url}`;
            const isVideo = bg.type === 'video';
            return (
              <div
                key={bg.id || i}
                style={{
                  position: 'absolute',
                  inset: 0,
                  opacity: isActive ? 1 : 0,
                  transition: (isActive || isPrevious) ? 'opacity 2s ease-in-out' : 'none',
                  zIndex: isActive ? 2 : isPrevious ? 1 : 0,
                  willChange: (isActive || isPrevious) ? 'opacity' : 'auto',
                  transform: 'translateZ(0)',
                  visibility: (isActive || isPrevious) ? 'visible' : 'hidden',
                }}
              >
                {isVideo ? (
                  <video
                    src={videoSrc}
                    muted={isMuted}
                    autoPlay={isActive}
                    loop
                    playsInline
                    className="w-full h-full object-cover"
                    ref={el => { videoRefs.current[i] = el; }}
                    onLoadedData={(e) => { if (isActive) e.target.play().catch(() => {}); }}
                  />
                ) : (
                  <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url(${imgUrl})` }} />
                )}
              </div>
            );
          })}
          {bgLink && (
            <a
              href={bgLink}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute inset-0 z-[3] cursor-pointer"
              data-testid="bg-clickable-link"
              onClick={() => {
                const activeBg = backgrounds[currentBgIndex];
                if (activeBg?.id) {
                  axios.post(`${API}/share/${shareId}/background-click`, { bg_id: activeBg.id, is_default: isDefaultBg }).catch(() => {});
                }
              }}
            />
          )}
          {/* Sound toggle for video backgrounds */}
          {backgrounds.some(bg => bg.type === 'video') && (
            <button
              onClick={() => {
                setIsMuted(prev => {
                  const next = !prev;
                  Object.values(videoRefs.current).forEach(v => { if (v) v.muted = next; });
                  return next;
                });
              }}
              className="absolute top-6 right-6 z-20 bg-black/50 backdrop-blur-sm border border-white/20 rounded-full p-3 text-white hover:bg-black/70 transition-all"
              data-testid="sound-toggle-btn"
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
          )}
          {backgrounds.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2">
              {backgrounds.map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full transition-all ${i === currentBgIndex ? 'bg-white w-6' : 'bg-white/40'}`} />
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-slate-900 to-blue-900">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
        </div>
      )}
      
      <div className="absolute inset-0 bg-black/30 pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 px-6 lg:px-16 xl:px-24 py-4 pointer-events-none">
        <div>
          <a href="/" className="inline-flex items-center gap-3 pointer-events-auto">
            <BoxIconOpen size={44} />
            <span className="text-2xl font-bold text-white tracking-tight">SENDBOX</span>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 min-h-[calc(100vh-80px)] flex items-center px-6 lg:px-16 xl:px-24 py-12 pointer-events-none">
        <div className="w-full max-w-md">
          <div className="bg-slate-900/95 backdrop-blur-xl border border-blue-500/30 rounded-3xl p-8 shadow-2xl pointer-events-auto" data-testid="download-card">
            {/* Icon & Title */}
            <div className="text-center mb-8">
              <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center shadow-lg ${isPaidFile && !purchaseComplete ? 'bg-gradient-to-br from-emerald-500 to-teal-500 shadow-emerald-500/30' : 'bg-gradient-to-br from-blue-500 to-cyan-500 shadow-blue-500/30'}`}>
                {isPaidFile && !purchaseComplete ? (
                  <ShoppingCart className="w-10 h-10 text-white" />
                ) : purchaseComplete ? (
                  <Check className="w-10 h-10 text-white" />
                ) : (
                  <Download className="w-10 h-10 text-white" />
                )}
              </div>
              <h1 className="text-2xl font-bold text-white mb-2" data-testid="download-page-title">
                {isPaidFile && !purchaseComplete ? 'Fichier en vente' : purchaseComplete ? 'Achat confirmé !' : 'Télécharger le fichier'}
              </h1>
              <p className="text-blue-200/70 text-sm">
                {isPaidFile && !purchaseComplete ? 'Ce fichier nécessite un achat' : 'Quelqu\'un vous a partagé des fichiers'}
              </p>
            </div>

            {/* File Info */}
            <div className="space-y-3 mb-6">
              <div className="bg-blue-950/40 rounded-xl p-4 border border-blue-500/20">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-5 h-5 text-cyan-400" />
                  <span className="text-white font-medium truncate" data-testid="filename">{shareInfo.filename}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-blue-300/70">
                    <HardDrive className="w-4 h-4" />
                    <span data-testid="filesize">{formatFileSize(shareInfo.file_size)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-blue-300/70">
                    <Calendar className="w-4 h-4" />
                    <span data-testid="expiry-date">Expire le {formatDate(shareInfo.expiry_date).split(' ')[0]}</span>
                  </div>
                </div>
                {shareInfo.file_count > 1 && (
                  <div className="mt-3 pt-3 border-t border-blue-500/20">
                    <p className="text-sm text-blue-400">{shareInfo.file_count} fichiers dans cette archive ZIP</p>
                  </div>
                )}
              </div>

              {/* Price display for paid files */}
              {isPaidFile && (
                <div className="bg-emerald-950/40 rounded-xl p-4 border border-emerald-500/30" data-testid="price-card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Tag className="w-5 h-5 text-emerald-400" />
                      <span className="text-emerald-200 font-medium">Prix</span>
                    </div>
                    <span className="text-2xl font-bold text-emerald-300" data-testid="file-price">
                      {shareInfo.price} {currencySymbol(shareInfo.currency)}
                    </span>
                  </div>
                </div>
              )}

              {/* Encryption badge */}
              {isEncrypted && (
                <div className="flex items-center gap-2 bg-emerald-950/30 border border-emerald-500/20 rounded-xl px-4 py-3" data-testid="encryption-badge">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-200 text-sm font-medium">Chiffrement de bout en bout</span>
                </div>
              )}

              {/* Password field if required */}
              {shareInfo.requires_password && canDownload && (
                <div className="space-y-2">
                  <label className="text-blue-200/80 text-sm flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Mot de passe requis
                  </label>
                  <Input type="password" placeholder="Entrez le mot de passe" value={password} onChange={(e) => setPassword(e.target.value)}
                    className="bg-blue-950/30 border-blue-500/30 text-white placeholder:text-blue-300/40" data-testid="password-input" />
                </div>
              )}

              {/* Message from sender */}
              {shareInfo.message && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                  <p className="text-blue-200/80 text-sm italic">"{shareInfo.message}"</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {isPaidFile && !purchaseComplete ? (
              <>
                {pollingStatus ? (
                  <div className="text-center py-4">
                    <Loader2 className="w-8 h-8 text-emerald-400 animate-spin mx-auto mb-3" />
                    <p className="text-emerald-200 text-sm">Vérification du paiement en cours...</p>
                  </div>
                ) : (
                  <Button onClick={handlePurchase} disabled={purchaseLoading}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold py-6 text-lg shadow-lg shadow-emerald-500/30 transition-all duration-300 hover:shadow-emerald-500/50"
                    data-testid="purchase-btn">
                    {purchaseLoading ? (
                      <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Redirection...</span>
                    ) : (
                      <span className="flex items-center gap-2 justify-center">
                        <ShoppingCart className="w-5 h-5" /> Acheter pour {shareInfo.price} {currencySymbol(shareInfo.currency)}
                      </span>
                    )}
                  </Button>
                )}
              </>
            ) : (
              <Button onClick={handleDownload}
                disabled={downloading || decrypting || (shareInfo.requires_password && !password)}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-6 text-lg shadow-lg shadow-blue-500/30 transition-all duration-300 hover:shadow-blue-500/50"
                data-testid="download-btn">
                {decrypting ? (
                  <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Déchiffrement...</span>
                ) : downloading ? (
                  <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Téléchargement...</span>
                ) : (
                  <span className="flex items-center gap-2 justify-center">
                    {purchaseComplete && <Check className="w-5 h-5" />}
                    {isEncrypted && <ShieldCheck className="w-5 h-5" />}
                    <Download className="w-5 h-5" /> Télécharger{isEncrypted ? ' & Déchiffrer' : ''}
                  </span>
                )}
              </Button>
            )}

            {/* Send your own files link */}
            <div className="mt-6 text-center">
              <Button variant="ghost" onClick={() => navigate('/')}
                className="text-blue-300/70 hover:text-white hover:bg-blue-500/10 transition-all duration-300" data-testid="send-own-files-btn">
                Envoyer vos propres fichiers →
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Pro Badge */}
      {backgrounds.length > 0 && (
        <div className="absolute bottom-6 right-6 z-10 pointer-events-auto">
          <div className="bg-slate-900/80 backdrop-blur-sm border border-blue-500/30 rounded-full px-4 py-2 flex items-center gap-2">
            <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
            <span className="text-sm text-blue-200/80">Fond personnalisé Pro</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadPage;
